import { getZipFromLatLon } from '../sidecarMap/getZipFromLatLon'

/** Optional context for error logging and tracking */
interface GetGeolocationContext {
  /** Component name for error tracking (e.g., 'member/LocationSearchField') */
  component?: string
}

/**
 * Prompts the user to allow location tracking and retrieves the user's current
 * latitude, longitude, and approximate zip code based on coordinates. If
 * Geolocation is not supported or an error occurs, an error response is
 * returned.
 *
 * Returns lat/lon even when zip resolution fails (e.g., Google Maps SDK not
 * loaded). Zip will be undefined in that case — callers should enrich later
 * when Maps becomes available.
 *
 * @param {GetGeolocationContext} [context] - Optional context for error
 *   tracking
 * @returns {Promise<{
 *   lat?: number
 *   lon?: number
 *   zip?: string
 *   error?: boolean
 *   response?: { message: string }
 * }>}
 *   A promise that resolves with the user's location (latitude, longitude, and
 *   optionally zip) or an error object.
 */
export const getGeolocation = (
  context?: GetGeolocationContext,
): Promise<{
  lat?: number
  lon?: number
  zip?: string
  error?: boolean
  code?: number
  response?: { message: string }
}> =>
  new Promise(resolve => {
    if (!navigator.geolocation) {
      const errorMsg = `Browser doesn't support Geolocation.`

      // eslint-disable-next-line no-console -- Datadog forwardConsoleLogs telemetry
      console.error('Browser Geolocation Not Supported', {
        errorType: 'geolocation_unsupported',
        component: context?.component || 'unknown',
        browser:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        message: errorMsg,
      })

      return resolve({
        error: true,
        response: {
          message: errorMsg,
        },
      })
    }

    navigator.geolocation.getCurrentPosition(
      async pos => {
        const {
          coords: { latitude: lat, longitude: lon },
        } = pos

        try {
          const latLonResult = await getZipFromLatLon(lat, lon)

          if (latLonResult.error) {
            // Zip resolution failed (Maps SDK not loaded) but we still have
            // valid coordinates — return them as a partial success.
            return resolve({ lat, lon })
          }

          resolve({ lat, lon, zip: latLonResult.zip })
        } catch {
          // Zip resolution threw — still return coordinates as partial success.
          resolve({ lat, lon })
        }
      },
      err => {
        if (err.code !== 1) {
          // eslint-disable-next-line no-console -- Datadog forwardConsoleLogs telemetry
          console.error('Browser Geolocation Error', {
            errorType: 'geolocation_failure',
            errorCode: err.code,
            message: err.message,
            component: context?.component || 'unknown',
          })
        }

        resolve({
          error: true,
          code: err.code,
          response: {
            message: err.message,
          },
        })
      },
      {
        timeout: 5000,
        maximumAge: 300000,
      },
    )
  })
